import {
  AgEvent,
  AgEventListener,
  AgPublicEventType,
  BodyScrollEndEvent,
  CellClickedEvent,
  CellKeyDownEvent,
  CellValueChangedEvent,
  ColumnHeaderClickedEvent,
  GridApi,
  RowClickedEvent,
  RowGroupOpenedEvent,
  SortChangedEvent,
} from 'ag-grid-community';
import { LogAction } from '../constants';
import { LoggerDataContextType } from '../context';
import { LoggerType, TrackingData } from '../models';

const elementType = 'DataGrid';

/**
 * Type for metadata provider function that can handle any AG Grid event
 */
export type GridEventMetadataProvider = (params: {
  eventType: AgPublicEventType;
  event: AgEvent;
}) => Record<string, unknown>;

/**
 * Type for metadata values that can be logged
 */
export type GridEventMetadata = {
  [key: string]: string | number | boolean | undefined | null;
};

/**
 * Helper function to merge base metadata with additional metadata from provider
 */
function buildEventMetadata(
  baseMetadata: GridEventMetadata,
  eventType: AgPublicEventType,
  event: AgEvent,
  gridEventMetadataProvider?: GridEventMetadataProvider
): string {
  const additionalMetadata = gridEventMetadataProvider?.({ eventType, event }) || {};
  return JSON.stringify({ ...baseMetadata, ...additionalMetadata });
}

export function addGridEventTracking(
  api: GridApi,
  events: AgPublicEventType[],
  trackEvent: (trackingData: TrackingData, contextData?: LoggerDataContextType, loggerInstances?: LoggerType[]) => void,
  gridEventMetadataProvider?: GridEventMetadataProvider
): void {
  if (api) {
    events.forEach((event) => {
      let handlerFn: AgEventListener | undefined;

      switch (event) {
        case 'cellClicked':
          handlerFn = ((e: CellClickedEvent) => {
            // Check if onCellClicked is defined for either the column or the grid
            if (typeof e.column.getColDef().onCellClicked === 'function' || api.getGridOption('onCellClicked')) {
              const baseMetadata = {
                column: e.column.getColId(),
                rowKey: e.node.key,
                rowIndex: e.rowIndex,
              };
              trackEvent({
                action: LogAction.AG_GRID_CELL_CLICKED,
                elementInfo: {
                  elementName: String(e.value),
                  elementType,
                  metadata: buildEventMetadata(baseMetadata, 'cellClicked', e, gridEventMetadataProvider),
                },
                srcElement: e.event?.target,
              });
            }
          }) as AgEventListener;
          break;
        case 'rowClicked':
          handlerFn = ((e: RowClickedEvent) => {
            trackEvent({
              action: LogAction.AG_GRID_ROW_CLICKED,
              elementInfo: { elementName: String(e.rowIndex), elementType },
              srcElement: e.event?.target,
            });
          }) as AgEventListener;
          break;
        case 'sortChanged':
          handlerFn = ((e: SortChangedEvent) => {
            // track the event only if it is triggered from the UI and not programmatically
            if (e.source === 'uiColumnSorted') {
              const sortedColumns = e.columns || [];
              if (sortedColumns.length > 0) {
                const colClicked = sortedColumns[sortedColumns.length - 1];
                const baseMetadata = {
                  sortDirection: colClicked.getSort(),
                };
                trackEvent({
                  action: LogAction.AG_GRID_SORT_CHANGED,
                  elementInfo: {
                    elementName: colClicked.getColDef().headerName,
                    elementType,
                    metadata: buildEventMetadata(baseMetadata, 'sortChanged', e, gridEventMetadataProvider),
                  },
                });
              }
            }
          }) as AgEventListener;
          break;
        case 'columnHeaderClicked':
          handlerFn = ((e: ColumnHeaderClickedEvent) => {
            trackEvent({
              action: LogAction.AG_GRID_HEADER_CLICK,
              elementInfo: {
                elementName: e.column.getId(),
                elementType,
              },
            });
          }) as AgEventListener<ColumnHeaderClickedEvent>;
          break;
        case 'cellValueChanged':
          handlerFn = ((e: CellValueChangedEvent) => {
            const baseMetadata = {
              column: e.column.getColId(),
            };
            trackEvent({
              action: LogAction.AG_GRID_CELL_VALUE_CHANGED,
              elementInfo: {
                elementValue: String(e.value),
                elementType,
                metadata: buildEventMetadata(baseMetadata, 'cellValueChanged', e, gridEventMetadataProvider),
              },
              srcElement: e.event?.target,
            });
          }) as AgEventListener<CellValueChangedEvent>;
          break;
        case 'bodyScrollEnd':
          handlerFn = ((e: BodyScrollEndEvent) => {
            trackEvent({
              action: LogAction.AG_GRID_BODY_SCROLL_END,
              elementInfo: {
                elementValue: `${e.direction}:${e.top}`,
                elementType,
              },
            });
          }) as AgEventListener<BodyScrollEndEvent>;
          break;
        case 'cellKeyDown':
          handlerFn = ((e: CellKeyDownEvent) => {
            const keyboardEvent = e.event as KeyboardEvent;
            switch (keyboardEvent.key) {
              case 'Enter': {
                const baseMetadata = {
                  column: e.column.getColId(),
                };
                trackEvent({
                  action: LogAction.AG_GRID_CELL_ENTER_KEY_DOWN,
                  elementInfo: {
                    elementName: String(e.value),
                    elementType,
                    metadata: buildEventMetadata(baseMetadata, 'cellKeyDown', e, gridEventMetadataProvider),
                  },
                  srcElement: e.event?.target,
                });
                break;
              }
              default:
                // Add more key handling as needed
                break;
            }
          }) as AgEventListener<CellKeyDownEvent>;
          break;
        case 'rowGroupOpened':
          handlerFn = ((e: RowGroupOpenedEvent) => {
            const baseMetadata = {
              id: e.node.id,
              rowIndex: e.rowIndex,
            };
            trackEvent({
              action: e.expanded ? LogAction.AG_GRID_ROW_GROUP_EXPANDED : LogAction.AG_GRID_ROW_GROUP_COLLAPSED,
              elementInfo: {
                elementType,
                metadata: buildEventMetadata(baseMetadata, 'rowGroupOpened', e, gridEventMetadataProvider),
              },
            });
          }) as AgEventListener<RowGroupOpenedEvent>;
          break;

        // Add more cases as needed for other events
        default:
          console.warn(`Unhandled event type: ${event}`);
      }

      if (handlerFn) {
        // Remove the existing event listener to avoid duplicate listeners being added
        api.removeEventListener(event, handlerFn);
        api.addEventListener(event, handlerFn);
      }
    });
  } else {
    console.warn('Grid API not initialized');
  }
}
